import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { Alert, Spacer } from '@zillow/constellation';
import { TextButton } from '@zillow/constellation-10';

import { useContactEmailPurge } from '../../hooks/queries/properties';
import PropertiesActions from '../../actions/PropertiesActions';

import ContactEmailDeliverability from '../../model/ContactEmailDeliverability';

const propTypes = {
  marginBottom: PropTypes.string,
  marginTop: PropTypes.string,
};
const defaultProps = {
  marginBottom: 'md',
  marginTop: 'none',
};

const LEARN_MORE_LINK = 'https://help.zillowrentalmanager.com/hc/en-us/articles/360052675773';

const ContactEmailDeliverabilityAlert = ({ marginBottom, marginTop }) => {
  const dispatch = useDispatch();

  const {
    contactEmailDeliverability: { contactEmail, isDeliverable, isPurgeLimitExceeded, supportEmail },
    listingAlias,
  } = useSelector((state) => state.properties.currentProperty);

  const [shouldRenderBanner, setShouldRenderBanner] = useState(false);

  const {
    refetch: refetchContactEmailPurge,
    isError: hasEmailPurgeError,
    data: emailPurgeData,
  } = useContactEmailPurge({ listingAlias });

  useEffect(() => {
    if (isDeliverable !== undefined) {
      setShouldRenderBanner(!isDeliverable);
    }
  }, [isDeliverable]);

  useEffect(() => {
    if (emailPurgeData) {
      dispatch(
        PropertiesActions.updateCurrentProperty({
          contactEmailDeliverability: ContactEmailDeliverability(emailPurgeData),
        }),
      );
    }
  }, [emailPurgeData]);

  const handleConfirmClick = () => {
    refetchContactEmailPurge();
  };

  const renderDefaultContent = () => {
    return (
      <span data-testid="DeliveryIssueContent">
        We&apos;ve detected a deliverability issue with your contact email: {contactEmail}.
      </span>
    );
  };

  const renderPurgeLimitExceededContent = () => {
    return (
      <div data-testid="PurgeLimitExceededContent">
        <span>We&apos;ve detected a deliverability issue with your contact email: {contactEmail}. Please contact</span>
        <TextButton asChild textStyle="body-sm">
          <a href={`mailto:${supportEmail}`} target="_blank" rel="noreferrer">
            &nbsp;customer support
          </a>
        </TextButton>
        <span>.</span>
      </div>
    );
  };

  const renderBody = () => {
    return isPurgeLimitExceeded ? renderPurgeLimitExceededContent() : renderDefaultContent();
  };

  const renderEmailPurgeError = () => {
    return (
      <div>
        <span>Action failed.&nbsp;</span>
        <TextButton textStyle="body-sm" onClick={handleConfirmClick}>
          Try again
        </TextButton>
      </div>
    );
  };

  const renderDefaultActionButton = () => {
    return (
      <div>
        <TextButton textStyle="body-sm" onClick={handleConfirmClick}>
          Confirm
        </TextButton>
        <span>
          &nbsp;the email is correct or&nbsp;
          <TextButton asChild textStyle="body-sm">
            <a href={LEARN_MORE_LINK} target="_blank" rel="noreferrer">
              learn more
            </a>
          </TextButton>
          .
        </span>
      </div>
    );
  };

  const renderActionButton = () => {
    return hasEmailPurgeError ? renderEmailPurgeError() : renderDefaultActionButton();
  };

  if (!shouldRenderBanner) {
    return null;
  }

  return (
    <Spacer marginBottom={marginBottom} marginTop={marginTop}>
      <Alert
        data-testid="ContactEmailDeliverabilityAlert"
        appearance="error"
        fontType="bodySmall"
        body={renderBody()}
        actionButton={!isPurgeLimitExceeded && renderActionButton()}
      />
    </Spacer>
  );
};

ContactEmailDeliverabilityAlert.propTypes = propTypes;
ContactEmailDeliverabilityAlert.defaultProps = defaultProps;

export default ContactEmailDeliverabilityAlert;
